// ─── local-pipeline: one STT → LLM(+tools) → TTS turn loop ───────────────────
// Not every voice model is speech-to-speech. This composes three swappable
// OpenAI-compatible stages so a non-omni stack — whisper + LM Studio + a local
// TTS, MiniCPM-o's server, or three hosted APIs — behaves like one voice provider.
//
// The loop runs in BOTH placements: inside the Durable Object for
// `placement: 'server'`, and inside the browser for `placement: 'client'` (the
// usual case, because a Cloudflare Worker cannot reach the boss's `localhost`).
// The turn order, the tool rounds and the guards are the same either way — so they
// live here, once, and each side supplies only what is genuinely local to it:
//
//   · where the conversation is kept   (the DO transcript vs a rolling tail)
//   · where assistant audio comes out  (session clients vs local playback)
//   · who is told about a transcript, a turn boundary, a failure
//
// That is `PipelineHost`, implemented once per side in
// `server/src/providers/local-pipeline.ts` and `web/src/driver/local-pipeline.ts`.
// Nothing in this file knows which one it has, and nothing here touches a DOM or a
// Worker global: `fetch` and plain data only.

import type { AudioFormat, Role } from './protocol.ts'
import { chat, speak, transcribe, type ChatMessage, type Endpoint, type RawToolCall, type ToolSpec } from './openai-http.ts'

export const MAX_TOOL_ROUNDS = 3
/** Guards against a stuck client streaming forever: ~60s of 24kHz mono PCM16. */
export const MAX_TURN_BYTES = 3_000_000

/** Fully resolved: whoever builds this has already applied every default. */
export interface PipelineSettings {
  stt: Endpoint
  llm: Endpoint
  tts: Endpoint
  persona: string
  voice?: string
  locale?: string
  audio: { input: AudioFormat; output: AudioFormat }
  /** Spoken on `start`, if the placement makes it ours to say. */
  greeting?: string
}

/** Vendor-neutral stage calls. Hosted providers can reuse the turn loop even when
 * one stage is not exposed through OpenAI's exact HTTP paths. */
export interface PipelineServices {
  transcribe(audio: ArrayBuffer, opts: { format: AudioFormat; locale?: string; signal?: AbortSignal; fetch: typeof fetch }): Promise<string>
  chat(opts: { messages: ChatMessage[]; tools: ToolSpec[]; signal?: AbortSignal; fetch: typeof fetch }): Promise<{ content: string; toolCalls: RawToolCall[] }>
  speak(text: string, opts: {
    voice?: string
    format: AudioFormat
    signal?: AbortSignal
    fetch: typeof fetch
    /** Hosted realtime TTS can emit PCM as it arrives; HTTP adapters return one buffer. */
    onAudio(audio: ArrayBuffer): void
  }): Promise<ArrayBuffer | null>
  /** A hosted adapter may overlap streamed LLM output with streamed TTS. */
  chatAndSpeak?(opts: {
    messages: ChatMessage[]
    tools: ToolSpec[]
    voice?: string
    format: AudioFormat
    signal?: AbortSignal
    fetch: typeof fetch
    onAudio(audio: ArrayBuffer): void
  }): Promise<{ content: string; toolCalls: RawToolCall[]; spoken: boolean }>
}

export interface PipelineToolCall {
  id: string
  name: string
  args: unknown
}

export interface PipelineToolOutcome {
  ok: boolean
  result?: unknown
  error?: string
}

/** Everything the pipeline cannot know for itself. One implementation per placement. */
export interface PipelineHost {
  /** Injected so the whole loop can be driven without a network. */
  fetch: typeof fetch
  /** Conversation so far, oldest first. The persona is prepended by the pipeline. */
  context(): ChatMessage[]
  /** A line the pipeline produced: put it in context, and tell the session. */
  record(role: Role, text: string): void
  /** A line the session handed us: put it in context, it already knows. */
  remember(role: Role, text: string): void
  /** Assistant turn boundary, for a host that has anyone to report it to. */
  speaking(on: boolean): void
  /** Assistant audio out — local playback, or down to the session's clients. */
  play(audio: ArrayBuffer): void
  /** Tool declarations to offer the model. */
  tools(): ToolSpec[]
  /** Runs a tool wherever it lives. Never throws: a failure comes back as `ok: false`. */
  runTool(call: PipelineToolCall): Promise<PipelineToolOutcome>
  /** A failure the user must see. A dead local endpoint is the common case. */
  fail(message: string): void
  /** Hold the host open for a turn that runs past the call that started it. */
  background(p: Promise<void>): void
}

export class LocalPipeline {
  private buffered: Uint8Array[] = []
  private bufferedBytes = 0
  private turn: AbortController | null = null

  constructor(
    private readonly settings: PipelineSettings,
    private readonly host: PipelineHost,
    private readonly services: PipelineServices = openAICompatibleServices(settings),
  ) {}

  async start(): Promise<void> {
    if (this.settings.greeting) await this.emitSpeech(this.settings.greeting)
  }

  pushAudio(chunk: ArrayBuffer): void {
    if (this.bufferedBytes >= MAX_TURN_BYTES) return
    this.buffered.push(new Uint8Array(chunk))
    this.bufferedBytes += chunk.byteLength
  }

  commit(): void {
    const audio = this.drain()
    if (audio) this.host.background(this.runTurn(audio))
  }

  /**
   * Text injected from outside the audio stream. The session has already recorded
   * it, so this is only the model-side effect: an assistant line is spoken as-is, a
   * user line is a turn (skipping STT), a system line only steers the next reply.
   */
  say(text: string, role: Role): Promise<void> {
    if (role === 'assistant') return this.emitSpeech(text)
    this.host.remember(role, text)
    if (role === 'user') this.host.background(this.respond(text).then(() => {}))
    return Promise.resolve()
  }

  /** Ask for an assistant turn without inventing a user transcript entry. */
  prompt(instructions: string): Promise<boolean> {
    return this.respond('', undefined, instructions)
  }

  /** Barge-in: stop generating and drop anything captured but not yet run. */
  cancel(): void {
    this.turn?.abort()
    this.turn = null
    this.buffered = []
    this.bufferedBytes = 0
  }

  close(): void {
    this.cancel()
  }

  private drain(): ArrayBuffer | null {
    if (!this.bufferedBytes) return null
    const merged = new Uint8Array(this.bufferedBytes)
    let at = 0
    for (const part of this.buffered) {
      merged.set(part, at)
      at += part.length
    }
    this.buffered = []
    this.bufferedBytes = 0
    return merged.buffer
  }

  /** STT → LLM (+tools) → TTS for one user turn. */
  private async runTurn(audio: ArrayBuffer): Promise<void> {
    const signal = this.beginTurn()
    try {
      const heard = await this.services.transcribe(audio, {
        format: this.settings.audio.input,
        locale: this.settings.locale,
        signal,
        fetch: this.host.fetch,
      })
      if (!heard) return
      this.host.record('user', heard)
      await this.respond(heard, signal)
    } catch (err) {
      this.fail(err)
    }
  }

  private async respond(userText: string, existing?: AbortSignal, instructions?: string): Promise<boolean> {
    const signal = existing ?? this.beginTurn()
    try {
      const messages: ChatMessage[] = [
        { role: 'system', content: this.settings.persona },
        ...this.host.context(),
        // A prompt-time event is a response-level instruction, not part of the durable
        // persona. Keeping it last stops the newest assistant line from drowning it out
        // and gives compatible chat templates a valid final generation boundary.
        ...(instructions ? [{ role: 'system' as const, content: instructions }] : []),
      ]
      // Recording the turn is the host's job, and most hosts' context already has it.
      if (userText && messages.at(-1)?.content !== userText) messages.push({ role: 'user', content: userText })

      for (let round = 0; round <= MAX_TOOL_ROUNDS; round++) {
        let started = false
        const play = (audio: ArrayBuffer) => {
          if (signal.aborted || !audio.byteLength) return
          if (!started) {
            started = true
            this.host.speaking(true)
          }
          this.host.play(audio)
        }
        const streamed = this.services.chatAndSpeak
          ? await this.services.chatAndSpeak({
              messages, tools: responseTools(this.host.tools(), Boolean(instructions)), voice: this.settings.voice,
              format: this.settings.audio.output, signal, fetch: this.host.fetch, onAudio: play,
            }).finally(() => { if (started) this.host.speaking(false) })
          : null
        const reply = streamed ?? await this.services.chat({
          messages, tools: responseTools(this.host.tools(), Boolean(instructions)), signal, fetch: this.host.fetch,
        })
        if (!reply.toolCalls.length) {
          if (reply.content) {
            this.host.record('assistant', reply.content)
            if (!streamed?.spoken) await this.emitSpeech(reply.content, signal)
            return true
          }
          return false
        }
        messages.push({ role: 'assistant', content: reply.content, tool_calls: reply.toolCalls })
        for (const call of reply.toolCalls) {
          const outcome = await this.host.runTool({ id: call.id, name: call.function.name, args: safeJson(call.function.arguments) })
          messages.push({
            role: 'tool',
            tool_call_id: call.id,
            content: JSON.stringify(outcome.ok ? outcome.result : { error: outcome.error }),
          })
        }
      }
      throw new Error(`the model kept calling tools past ${MAX_TOOL_ROUNDS} rounds`)
    } catch (err) {
      this.fail(err)
      return false
    }
  }

  private async emitSpeech(text: string, signal?: AbortSignal): Promise<void> {
    let started = false
    const play = (audio: ArrayBuffer) => {
      if (signal?.aborted || !audio.byteLength) return
      if (!started) {
        started = true
        // A speech boundary means audible bytes exist, not that TTS has started
        // thinking. This keeps the UI and barge-in aligned with what a person hears.
        this.host.speaking(true)
      }
      this.host.play(audio)
    }
    try {
      const audio = await this.services.speak(text, {
        voice: this.settings.voice,
        format: this.settings.audio.output,
        signal,
        fetch: this.host.fetch,
        onAudio: play,
      })
      if (audio) play(audio)
    } finally {
      if (started) this.host.speaking(false)
    }
  }

  private beginTurn(): AbortSignal {
    this.turn?.abort()
    this.turn = new AbortController()
    return this.turn.signal
  }

  /**
   * Every failure is reported. One that was swallowed would present as a mic that
   * simply does nothing, which is the worst outcome there is.
   */
  private fail(err: unknown): void {
    if (err instanceof Error && err.name === 'AbortError') return // barge-in, not a failure
    this.host.fail(err instanceof Error ? err.message : String(err))
  }
}

/** These tools describe private initiative events and must never be offered on a user turn. */
export function responseTools<T extends { name: string }>(tools: T[], prompted: boolean): T[] {
  return prompted ? tools : tools.filter((tool) => tool.name !== 'hold_silence' && tool.name !== 'end_call')
}

function openAICompatibleServices(settings: PipelineSettings): PipelineServices {
  return {
    transcribe: (audio, opts) => transcribe(settings.stt, audio, opts),
    chat: (opts) => chat(settings.llm, opts),
    speak: (text, opts) => speak(settings.tts, text, opts),
  }
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return { _raw: raw }
  }
}
